import React from "react";
import { Phone, CheckCircle2, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import SectionLabel from "@/components/SectionLabel";
import EmergencyBanner from "@/components/EmergencyBanner";
import { SERVICES } from "@/lib/services";
import { PRIMARY_PHONE } from "@/lib/site";

const HEADER_IMG = "https://images.pexels.com/photos/17429097/pexels-photo-17429097.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600";

const Services = () => {
  return (
    <div data-testid="services-page">
      <section className="relative overflow-hidden bg-slate-900 text-white">
        <div className="absolute inset-0">
          <img src={HEADER_IMG} alt="Flatbed tow truck loading a vehicle" className="h-full w-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-900/80 to-slate-900/40" />
        </div>
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <SectionLabel color="amber">Our Services</SectionLabel>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter max-w-3xl leading-[1.05]">
            We tow everything. Quickly, carefully, and at the cheapest price possible.
          </h1>
          <p className="mt-5 max-w-2xl text-blue-100 text-lg leading-relaxed">
            Browse our full range of towing & transport services. Every job — big or small — gets the same upfront pricing and the same fast 24/7 response.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a
              href={`tel:${PRIMARY_PHONE.tel}`}
              data-testid="services-hero-call"
              className="inline-flex items-center gap-3 rounded-full bg-[var(--color-secondary)] px-6 py-3.5 font-display font-extrabold hover:-translate-y-0.5 transition-all"
            >
              <Phone className="h-4 w-4" />
              Call now for a quote
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-full border-2 border-white/30 bg-white/10 px-6 py-3.5 font-display font-bold backdrop-blur hover:bg-white/20"
            >
              Send a quote request <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 space-y-16">
          {SERVICES.map((s, idx) => {
            const Icon = s.icon;
            const reverse = idx % 2 === 1;
            return (
              <article
                id={s.slug}
                key={s.slug}
                data-testid={`service-section-${s.slug}`}
                className="grid gap-10 lg:gap-16 lg:grid-cols-12 items-start scroll-mt-24"
              >
                <div className={`lg:col-span-7 ${reverse ? "lg:order-2" : ""}`}>
                  <div className="flex items-center gap-3">
                    <div className="grid h-12 w-12 place-items-center rounded-xl bg-blue-50 text-[var(--color-primary)]">
                      <Icon className="h-6 w-6" />
                    </div>
                    <span className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold">
                      Service {String(idx + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <h2 className="mt-5 font-display text-3xl sm:text-4xl font-black tracking-tighter text-slate-900">
                    {s.title}
                  </h2>
                  <p className="mt-4 text-slate-600 leading-relaxed max-w-2xl">
                    {s.description}
                  </p>
                  <ul className="mt-6 grid sm:grid-cols-2 gap-3">
                    {s.benefits.map((b) => (
                      <li key={b} className="flex items-center gap-2 text-slate-700">
                        <CheckCircle2 className="h-4 w-4 text-[var(--color-primary)]" />
                        <span className="font-semibold">{b}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-7 flex flex-wrap items-center gap-3">
                    <a
                      href={`tel:${PRIMARY_PHONE.tel}`}
                      data-testid={`service-${s.slug}-call`}
                      className="inline-flex items-center gap-2 rounded-full bg-[var(--color-secondary)] px-5 py-3 text-white font-display font-bold hover:-translate-y-0.5 transition-all"
                    >
                      <Phone className="h-4 w-4" /> Call Now for Quote
                    </a>
                    <Link
                      to="/contact"
                      className="inline-flex items-center gap-2 text-[var(--color-primary)] font-bold hover:text-[var(--color-primary-soft)]"
                    >
                      Or send us details <ArrowRight className="h-4 w-4" />
                    </Link>
                  </div>
                </div>

                <div className={`lg:col-span-5 ${reverse ? "lg:order-1" : ""}`}>
                  <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-8">
                    <p className="font-display text-5xl font-black text-slate-900 leading-none">
                      <span className="text-[var(--color-secondary)] font-num">$70</span>
                    </p>
                    <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold mt-2">
                      Quotes Start From
                    </p>
                    <p className="mt-5 text-slate-600 text-sm leading-relaxed">
                      {s.short} We service all of Victoria — call us anytime, day or night, and we'll give you a clear price upfront.
                    </p>
                    <div className="mt-6 grid gap-2">
                      <div className="flex items-center justify-between border-t border-slate-200 pt-3 text-sm">
                        <span className="text-slate-500">Availability</span>
                        <span className="font-bold text-slate-900">24/7</span>
                      </div>
                      <div className="flex items-center justify-between border-t border-slate-200 pt-3 text-sm">
                        <span className="text-slate-500">Coverage</span>
                        <span className="font-bold text-slate-900">All of Victoria</span>
                      </div>
                      <div className="flex items-center justify-between border-t border-slate-200 pt-3 text-sm">
                        <span className="text-slate-500">Insurance</span>
                        <span className="font-bold text-slate-900">Fully covered</span>
                      </div>
                    </div>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      <EmergencyBanner />
    </div>
  );
};

export default Services;
